/**
 * Marketing report — the data contract and the arithmetic, in one place.
 *
 * The published page (ops/report-nightly/pages/marketing.html) computes every
 * ratio, verdict and share from daily atoms held in one seed object. In-app the
 * atoms arrive from five endpoints instead, so the SHAPES differ — but the
 * arithmetic must not. Each function below is a port of the named renderer in
 * that file, and the rules it encodes (ratio of sums, never a mean of ratios;
 * thresholds in exactly one block; a missing figure stays missing) are the
 * point of the port, not an implementation detail.
 */

/* ── the window the server resolved ───────────────────────────────────────
 * /v1/reports/* returns the full ResolvedWindow, not just {start,end,as_of}:
 * the comparison period it chose and whether that period exists at all are
 * part of it. The shared ReportResponse type declares only the three fields
 * every page uses, so the rest are read through this.
 */
export interface MktWindow {
  start: string;
  end: string;
  as_of: string;
  previous_start?: string;
  previous_end?: string;
  /**
   * False when the previous period falls outside what the source holds. A
   * comparison against days that do not exist reads as a collapse, so the page
   * must show no delta and say why.
   */
  comparison_available?: boolean;
  note?: string;
}

/** One row of /v1/reports/marketing/overview `periods`. */
export interface MktPeriod {
  spend: number | null;
  ad_sales: number | null;
  direct_sales: number | null;
  roas: number | null;
  acos: number | null;
  atc: number | null;
  cost_per_atc: number | null;
  new_customers: number | null;
  impressions: number | null;
  clicks: number | null;
  cpm: number | null;
  units: number | null;
  direct_qty: number | null;
  campaigns: number | null;
}

/** One row of `series` — the daily line. No impressions, no ATC, no new users. */
export interface MktDay {
  day: string;
  spend: number | null;
  ad_sales: number | null;
  roas: number | null;
  cpm: number | null;
  /** The CPM denominator. Needed to pool a running average — see MarketingView. */
  impressions: number | null;
}

/** One of the eight ad slots, pooled over the selected window. */
export interface DaypartRow {
  slot_order: number | null;
  slot_label: string;
  spend: number | null;
  impressions: number | null;
  cpm: number | null;
  spend_share_pct: number | null;
  days: number | null;
}

export function daypartOf(r: Record<string, unknown>): DaypartRow {
  return {
    slot_order: num(r["slot_order"]),
    slot_label: str(r["slot_label"]) ?? "—",
    spend: num(r["spend"]),
    impressions: num(r["impressions"]),
    cpm: num(r["cpm"]),
    spend_share_pct: num(r["spend_share_pct"]),
    days: num(r["days"]),
  };
}

export interface StructureRow {
  campaign_type: string;
  spend: number | null;
  ad_sales: number | null;
  roas: number | null;
  acos: number | null;
  atc: number | null;
  /** SUM(atc) / SUM(clicks) — NOT the impressions basis the report uses. */
  atc_rate: number | null;
  new_customers: number | null;
  cpm: number | null;
  spend_share: number | null;
}

export interface KeywordRow {
  keyword: string;
  match_type: string | null;
  spend: number | null;
  ad_sales: number | null;
  roas: number | null;
  cost_per_atc: number | null;
  atc: number | null;
  impressions: number | null;
  cpm: number | null;
  /** Against every keyword in the window, not against the rows returned. */
  revenue_share: number | null;
}

export interface ProductRow {
  product_id: string;
  sku: string;
  grammage: string | null;
  spend: number | null;
  ad_sales: number | null;
  roas: number | null;
  units: number | null;
  cvr: number | null;
  cpm: number | null;
  /** True when the crawl does not know the product: a bundle or a delisting. */
  outside_catalogue: boolean | null;
  /** Pro-rated planned spend from ocean_dashboard.sku_plan. */
  planned_spend: number | null;
}

/* ── share of voice ───────────────────────────────────────────────────────
 * Read from /v1/visibility/overview rather than a second SOV query. There are
 * already two different SOVs on this platform and a third implementation of
 * the Hector-derived one is how two pages start quietly disagreeing about the
 * same brand in the same window.
 */
export interface Cell {
  value: number | null;
  previous: number | null;
  delta: number | null;
  delta_pct: number | null;
  unavailable_reason?: string;
}

export interface SovKeyword {
  id: string;
  label: string;
  ad_sov: Cell;
  organic_sov: Cell;
  overall_sov: Cell;
  avg_overall_rank: Cell;
  appearances: number | null;
  /** National last-30-day demand. Never summed. */
  search_volume?: number | null;
}

export interface VisibilityOverview {
  window: MktWindow;
  sov_source: string;
  kpis: Record<string, Cell | undefined>;
  keywords: SovKeyword[];
  response_meta: {
    coverage?: {
      keywords_in_scope: number;
      cities_in_scope: number;
      crawls: number;
      days_with_crawls: number;
    };
  };
}

/* ── primitives ─────────────────────────────────────────────────────────── */

/**
 * A number, or null.
 *
 * `Number(null)` is 0 and `Number('')` is 0, which is how a missing figure
 * becomes a real-looking zero. Both are rejected here.
 */
export function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/** Port of periodLabel(f,t). En-dash between the day numbers, as the page has. */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function periodLabel(
  f: string | undefined,
  t: string | undefined,
): string {
  if (!f || !t) return "—";
  const a = new Date(`${f}T00:00:00Z`);
  const b = new Date(`${t}T00:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()))
    return `${f} to ${t}`;
  const mo = (i: number): string => MONTHS[i] ?? "";
  return a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCFullYear() === b.getUTCFullYear()
    ? `${a.getUTCDate()}–${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`
    : `${a.getUTCDate()} ${mo(a.getUTCMonth())} – ${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`;
}

/** Reads one `periods` row out of the overview response into a typed shape. */
export function periodOf(
  rows: Array<Record<string, unknown>> | undefined,
  which: "current" | "previous",
): MktPeriod | null {
  const r = (rows ?? []).find((x) => x["period"] === which);
  if (!r) return null;
  return {
    spend: num(r["spend"]),
    ad_sales: num(r["ad_sales"]),
    direct_sales: num(r["direct_sales"]),
    roas: num(r["roas"]),
    acos: num(r["acos"]),
    atc: num(r["atc"]),
    cost_per_atc: num(r["cost_per_atc"]),
    new_customers: num(r["new_customers"]),
    impressions: num(r["impressions"]),
    clicks: num(r["clicks"]),
    cpm: num(r["cpm"]),
    units: num(r["units"]),
    direct_qty: num(r["direct_qty"]),
    campaigns: num(r["campaigns"]),
  };
}

export function dayOf(r: Record<string, unknown>): MktDay {
  return {
    day: str(r["day"]) ?? "",
    spend: num(r["spend"]),
    ad_sales: num(r["ad_sales"]),
    roas: num(r["roas"]),
    cpm: num(r["cpm"]),
    impressions: num(r["impressions"]),
  };
}

/** Ratio of sums, with a zero or missing denominator yielding null, not zero. */
export function ratio(a: number | null, b: number | null): number | null {
  if (a === null || b === null || b === 0) return null;
  return a / b;
}

/**
 * Port of toneOf(c,p,good) — which way a sparkline should be coloured.
 *
 * Kept separate from the <Delta> component because the two have to agree: the
 * tile's delta and the line under it encode the same move, and a green number
 * over a red line is the kind of thing a reader stops trusting the page over.
 * A move under 0.05% is flat, and a `neutral` metric (spend) is never coloured
 * at all — spending more is neither good nor bad without the return beside it.
 */
export function toneOf(
  c: number | null,
  p: number | null,
  good: "up" | "down" | "neutral",
): "up" | "down" | "flat" {
  if (c === null || p === null || p === 0) return "flat";
  const ch = ((c - p) / Math.abs(p)) * 100;
  if (Math.abs(ch) < 0.05 || good === "neutral") return "flat";
  return (good === "down" ? ch < 0 : ch > 0) ? "up" : "down";
}

/* ── the verdict engine ───────────────────────────────────────────────────
 * Port of keywordVerdicts(). THE THRESHOLDS LIVE HERE AND NOWHERE ELSE —
 * they are Hector's to set, not the platform's, and the card's footnote quotes
 * these same constants so the page can never describe a rule it is not
 * applying.
 */
export const ACTION_RULES = {
  roas_strong: 3.0,
  roas_weak: 1.5,
  spend_material: 0.03,
  organic_thin: 1.0,
} as const;

export type Verdict = "STOP" | "SCALE" | "OPTIMISE";

export interface Judged {
  keyword: string;
  match_type: string | null;
  spend: number;
  roas: number | null;
  /** Of keyword spend in the window. See the card footnote for the denominator. */
  share: number | null;
  /** Pooled organic SOV for the same window, or null when never crawled. */
  organic: number | null;
  action: Verdict;
  why: string;
}

const ratioText = (v: number | null): string =>
  v === null ? "—" : `${v.toFixed(2)}x`;

/**
 * One keyword's verdict.
 *
 * Ordered exactly as the published page orders it, because the branches are
 * not mutually exclusive: a weak keyword on material spend must read as "the
 * largest single saving available" rather than as a generic floor breach, and a
 * rented-visibility keyword only reaches OPTIMISE after the ROAS floor has had
 * its say.
 */
export function judge(
  keyword: string,
  match_type: string | null,
  spend: number,
  roas: number | null,
  share: number | null,
  organic: number | null,
): Judged {
  const rented = organic !== null && organic < ACTION_RULES.organic_thin;
  const sharePct = share === null ? null : share * 100;
  const shareText =
    sharePct === null ? "an unknown share" : `${sharePct.toFixed(1)}%`;
  let action: Verdict;
  let why: string;

  if (roas === null) {
    // No ROAS means no verdict. Defaulting to OPTIMISE would read as a
    // judgement that was never made.
    action = "OPTIMISE";
    why =
      "No return measured in this window — spend with no attributed sales, or no spend at all.";
  } else if (
    roas >= ACTION_RULES.roas_strong &&
    share !== null &&
    share < 0.1
  ) {
    action = "SCALE";
    why = `Returns ${ratioText(roas)} on only ${shareText} of keyword spend. Headroom to invest.`;
  } else if (
    roas < ACTION_RULES.roas_weak &&
    share !== null &&
    share >= ACTION_RULES.spend_material
  ) {
    action = "STOP";
    why = `${shareText} of keyword spend returning ${ratioText(roas)}${rented ? ", with no organic position behind it" : ""}. The largest single saving available.`;
  } else if (roas < ACTION_RULES.roas_weak) {
    action = "STOP";
    why = `Returning ${ratioText(roas)} — below the ${ACTION_RULES.roas_weak}x floor.`;
  } else if (rented && organic !== null && roas < ACTION_RULES.roas_strong) {
    action = "OPTIMISE";
    why = `${ratioText(roas)} return and ${organic.toFixed(2)}% organic share — the visibility is rented. Cut the bid, not the keyword.`;
  } else {
    action = "OPTIMISE";
    why = `${ratioText(roas)} return on ${shareText} of spend. Holding, not compounding.`;
  }
  return { keyword, match_type, spend, roas, share, organic, action, why };
}

const BAND: Record<Verdict, number> = { STOP: 0, SCALE: 1, OPTIMISE: 2 };

/** STOP, then SCALE, then OPTIMISE; spend descending inside each band. */
export function byVerdict(a: Judged, b: Judged): number {
  return BAND[a.action] - BAND[b.action] || b.spend - a.spend;
}

/**
 * The colour band on a ROAS figure — 3x and above green, under 1.5x red.
 *
 * The same two numbers as the verdict thresholds, read from the same block, so
 * a red figure and a STOP verdict can never disagree.
 */
export function roasColour(v: number | null): string {
  if (v === null) return "var(--ink-3)";
  return v >= ACTION_RULES.roas_strong
    ? "var(--up)"
    : v < ACTION_RULES.roas_weak
      ? "var(--down)"
      : "var(--ink)";
}

/** The campaign types the report names, with the funnel stage it labels them. */
export const CAMPAIGN_TYPES: Record<string, { label: string; stage: string }> =
  {
    PRODUCT_LISTING: { label: "Product Listing", stage: "BOFU / search" },
    PRODUCT_RECOMMENDATION: {
      label: "Product Recommendation",
      stage: "TOFU / visibility",
    },
    BANNER_LISTING: { label: "Banner", stage: "TOFU / visibility" },
    BANNER: { label: "Banner", stage: "TOFU / visibility" },
  };

/** Render order on the published page: down the funnel, then anything else. */
export const TYPE_ORDER = [
  "PRODUCT_LISTING",
  "PRODUCT_RECOMMENDATION",
  "BANNER_LISTING",
  "BANNER",
];

/**
 * The alert legend, verbatim from the published page's frozen literal.
 *
 * Static config, not data: the page holds these sentences as a hand-written
 * block and only the counts beside them are rebuilt nightly. The counts are
 * what this app cannot read yet (see the Budget health card).
 */
export const ALERT_LEGEND = {
  DAILY_BUDGET_EXHAUST: "Daily budget spent before the day ended.",
  OVERALL_BUDGET_EXHAUST: "Campaign budget will exhaust before the end date.",
  ON_HOLD:
    "Daily budget exhausted mid-day; flipped out of ACTIVE. Still serving and still billing. " +
    "Budget decision required.",
} as const;
